/**
 * Filterable, paginated testimonial carousel. Category pills filter which
 * cards are visible (client-side, no reload); dots + arrow navigate the
 * currently-visible set via native scroll-snap. Video cards are click-to-play,
 * not autoplay.
 */
class TestimonialCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.testimonial-carousel__track');
    this.cards = Array.from(this.querySelectorAll('.testimonial-card'));
    this.pills = Array.from(this.querySelectorAll('.testimonial-carousel__pill'));
    this.dotsContainer = this.querySelector('.testimonial-carousel__dots');
    this.nextButton = this.querySelector('.testimonial-carousel__arrow');
    this.visibleCards = [];
    this.storageKey = this.dataset.sectionId ? `testimonial-carousel-category-${this.dataset.sectionId}` : null;

    this.pills.forEach((pill) => {
      pill.addEventListener('click', () => this.filter(pill.dataset.category));
    });

    this.nextButton?.addEventListener('click', () => this.scrollToNext());
    this.track?.addEventListener('scroll', () => this.#syncActiveDot(), { passive: true });

    this.querySelectorAll('.testimonial-card__play').forEach((button) => {
      button.addEventListener('click', () => this.#playVideo(button));
    });

    // Remembers the selected tab across the Shopify theme editor's section
    // re-renders (which happen on every settings change), so editing one
    // testimonial doesn't silently reset the preview back to the first tab.
    let restoredCategory = null;
    if (this.storageKey) {
      try {
        restoredCategory = sessionStorage.getItem(this.storageKey);
      } catch (e) {
        restoredCategory = null;
      }
    }

    const initialCategory =
      restoredCategory && this.pills.some((pill) => pill.dataset.category === restoredCategory)
        ? restoredCategory
        : this.pills[0]?.dataset.category;

    if (initialCategory) this.filter(initialCategory, true);
  }

  /**
   * @param {string} category
   * @param {boolean} [isInitial]
   */
  filter(category, isInitial) {
    this.pills.forEach((pill) => {
      pill.classList.toggle('is-active', pill.dataset.category === category);
    });

    if (this.storageKey) {
      try {
        sessionStorage.setItem(this.storageKey, category);
      } catch (e) {
        // Ignore (e.g. storage disabled/full) — falls back to always
        // starting on the first tab, same as before this fix.
      }
    }

    this.visibleCards = this.cards.filter((card) => {
      const cats = (card.dataset.categories || '').split(',').filter(Boolean);
      const match = cats.includes(category);
      card.hidden = !match;
      return match;
    });

    this.#buildDots();
    this.track?.scrollTo({ left: 0, behavior: isInitial ? 'instant' : 'smooth' });
  }

  #buildDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';

    this.visibleCards.forEach((card, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'testimonial-carousel__dot';
      dot.setAttribute('aria-label', `Go to testimonial ${index + 1}`);
      if (index === 0) dot.classList.add('is-active');
      dot.addEventListener('click', () => {
        card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      });
      this.dotsContainer.appendChild(dot);
    });
  }

  #syncActiveDot() {
    if (!this.visibleCards.length || !this.dotsContainer || !this.track) return;

    const trackLeft = this.track.getBoundingClientRect().left;
    let closestIndex = 0;
    let closestDistance = Infinity;

    this.visibleCards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.dotsContainer.querySelectorAll('.testimonial-carousel__dot').forEach((dot, index) => {
      dot.classList.toggle('is-active', index === closestIndex);
    });
  }

  scrollToNext() {
    if (!this.visibleCards.length || !this.dotsContainer) return;

    const dots = Array.from(this.dotsContainer.querySelectorAll('.testimonial-carousel__dot'));
    const activeIndex = dots.findIndex((dot) => dot.classList.contains('is-active'));
    const nextIndex = (activeIndex + 1) % this.visibleCards.length;

    this.visibleCards[nextIndex]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  }

  /**
   * @param {HTMLElement} button
   */
  #playVideo(button) {
    const wrap = button.closest('.testimonial-card__video-wrap');
    const video = wrap?.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    video.setAttribute('controls', '');
    video.play().catch(() => {});
    wrap.classList.add('is-playing');
  }
}

if (!customElements.get('testimonial-carousel')) {
  customElements.define('testimonial-carousel', TestimonialCarousel);
}
